import { queryOptions } from '@tanstack/react-query'

import {
  SelfInviteLink,
  UserSignupDetails,
} from '@/api/generated-api-and-types'
import type { AppLocale } from '@/locale/types/Locale'

import { createApiMethod, type } from './utils/createApiMethod'

/**
 * Shape of the signup-details response as currently consumed by the app.
 *
 * NOTE: This intentionally differs from the generated `UserSignupDetails`
 * type (which exposes `roles[]`/`state`/`company: string`). The existing
 * selectors read `role`/`active`/`company` (object), so we keep this shape to
 * preserve the current runtime behaviour.
 */
export interface SignupDetails {
  id?: string
  email?: string
  role?: UserSignupDetails['roles'][number]
  active?: boolean
  company?: Record<string, unknown>
  // Language selected for the invited user during the invite (MP-17740).
  // Consumed by the locale detection chain so the signup page renders in the
  // invitee's chosen language. May be absent or unsupported — resolveLocale
  // filters those out.
  lang?: AppLocale
}

export const signup = {
  details: createApiMethod({
    method: 'GET',
    path: '/users/:uuid/signup-details',
    response: type<SignupDetails>(),
  }),
  selfInvite: {
    link: createApiMethod({
      method: 'GET',
      path: '/invites/self-invite/links/:slug',
      response: type<SelfInviteLink>(),
    }),
    create: createApiMethod({
      method: 'POST',
      path: '/invites/self-invite',
      data: type<{ email: string; id: string }>(),
    }),
  },
}

export const signupQueries = {
  all: () => ['signup'] as const,
  // The key is intentionally global (not keyed by uuid): the signup flow only
  // deals with one invite at a time and later steps read the same entry without
  // the uuid in scope (mirrors the prior single-entry Redux cache). Only the
  // owning step enables the fetch.
  details: (uuid?: string) =>
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryOptions({
      queryKey: [...signupQueries.all(), 'details'] as const,
      queryFn: () => signup.details({ params: { uuid: uuid ?? '' } }),
    }),
  selfInviteLink: (slug: string) =>
    queryOptions({
      queryKey: [...signupQueries.all(), 'self-invite', slug] as const,
      queryFn: () => signup.selfInvite.link({ params: { slug } }),
    }),
}
